/**
 * Saved Reports API service functions
 * Handles CRUD operations for saved reports (dashboards_saved table)
 */
import { buildConfigApiUrl } from '@api/api';

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

/**
 * Create a new saved report
 * @param {Object} savedReportData - The saved report data
 * @param {string} savedReportData.name - Name of the saved report
 * @param {string} savedReportData.description - Description of the saved report
 * @param {number} savedReportData.dashboardId - ID of the dashboard being used
 * @param {Object} savedReportData.configuration - Filter configuration
 */
export const createSavedReport = async (savedReportData) => {
  try {
    const response = await fetch(buildConfigApiUrl('/saved_reports'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        name: savedReportData.name,
        description: savedReportData.description || '',
        dashboardId: savedReportData.dashboardId,
        configuration: savedReportData.configuration || {},
      }),
    });

    if (!response.ok) {
      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'A saved report with this name already exists');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Fetch all saved reports
 */
export const fetchSavedReports = async () => {
  try {
    const response = await fetch(buildConfigApiUrl('/saved_reports'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to load saved reports: ${error.message}`,
      data: [],
    };
  }
};

/**
 * Fetch a specific saved report by ID
 */
export const fetchSavedReportById = async (id) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/saved_reports/${id}`), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Saved report not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Update an existing saved report
 */
export const updateSavedReport = async (id, savedReportData) => {
  try {
    const body = {};

    if (savedReportData.name !== undefined) body.name = savedReportData.name;
    if (savedReportData.description !== undefined) body.description = savedReportData.description;
    if (savedReportData.status !== undefined) body.status = savedReportData.status;
    if (savedReportData.configuration !== undefined) body.configuration = savedReportData.configuration;

    const response = await fetch(buildConfigApiUrl(`/saved_reports/${id}`), {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Saved report not found');
      }
      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'A saved report with this name already exists');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Delete a saved report
 */
export const deleteSavedReport = async (id) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/saved_reports/${id}`), {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Saved report not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      status: 'success',
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Search saved reports with pagination, filtering, and sorting
 */
export const searchSavedReports = async ({
  page = 1,
  pageSize = 50,
  sortField = 'createdAt',
  sortOrder = 'desc',
  filters = [],
  searchTerm = '',
  dashboardId = null,
}) => {
  try {
    const body = {
      page,
      pageSize,
      sortField,
      sortOrder,
      filters,
      searchTerm,
    };

    // Only include dashboardId if provided
    if (dashboardId !== null) {
      body.dashboardId = dashboardId;
    }

    const response = await fetch(buildConfigApiUrl('/saved_reports/search'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      rows: data.data || [],
      total: data.total || 0,
    };
  } catch {
    return {
      rows: [],
      total: 0,
    };
  }
};

/**
 * Bulk delete saved reports
 */
export const bulkDeleteSavedReports = async ({ ids }) => {
  try {
    const response = await fetch(buildConfigApiUrl('/saved_reports/bulk/delete'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ ids }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Toggle saved report status (enable/disable)
 */
export const toggleSavedReportStatus = async (id, status) => updateSavedReport(id, { status });

/**
 * Fetch saved reports filtered by dashboard ID
 * @param {number} dashboardId - The dashboard ID to filter by
 */
export const fetchSavedReportsByDashboard = async (dashboardId) => {
  try {
    const result = await searchSavedReports({
      dashboardId,
      pageSize: 100, // Get all for the modal
      sortField: 'createdAt',
      sortOrder: 'desc',
    });

    return {
      status: 'success',
      data: result.rows || [],
      total: result.total || 0,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
      data: [],
    };
  }
};

/**
 * Helper to safely map array values to dropdown format
 * @param {Array} values - Array of values from API
 * @returns {Array} - Array of dropdown options
 */
const mapToDropdownOptions = (values) => {
  if (!Array.isArray(values)) return [];
  return values
    .filter((value) => value != null && value !== '')
    .map((value) => ({
      value: String(value),
      label: String(value),
    }));
};

/**
 * Maps API response to dropdown format for saved reports filters
 * @param {Object} apiResponse - Response from the API
 * @returns {Object} - Mapped filter options in dropdown format
 */
const mapFilterOptionsToDropdownFormat = (apiResponse) => {
  if (!apiResponse) {
    return {};
  }

  const result = {};

  if (apiResponse.name) {
    result.name = mapToDropdownOptions(apiResponse.name);
  }

  if (apiResponse.description) {
    result.description = mapToDropdownOptions(apiResponse.description);
  }

  if (apiResponse.dashboardName) {
    result.dashboardName = mapToDropdownOptions(apiResponse.dashboardName);
  }

  return result;
};

/**
 * Converts UI filters array to API format for saved reports
 * @param {Array} uiFilters - Array of filter objects from UI { type, value }
 * @returns {Array} - Filters in API format
 */
export const convertSavedReportFiltersToApiFormat = (uiFilters = []) => {
  const apiFilters = [];

  uiFilters.forEach((filter) => {
    if (!filter.type || !filter.value || filter.value.length === 0) return;

    apiFilters.push({
      type: filter.type,
      value: Array.isArray(filter.value) ? filter.value[0] : filter.value,
    });
  });

  return apiFilters;
};

/**
 * Fetches available filter values for saved reports search
 * @param {Array} currentFilters - Current filters to apply when fetching options
 * @param {string} searchTerm - Optional search term to filter options
 * @returns {Promise<Object>} - Object with filter options for each filter type
 */
export const getSavedReportFilterOptions = async (
  currentFilters = [],
  searchTerm = '',
) => {
  try {
    const requestBody = {
      filters: convertSavedReportFiltersToApiFormat(currentFilters),
    };
    if (searchTerm) {
      requestBody.searchTerm = searchTerm;
    }

    const response = await fetch(buildConfigApiUrl('/saved_reports/search/filters'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch filter options');
    }

    const data = await response.json();
    return mapFilterOptionsToDropdownFormat(data);
  } catch (error) {
    console.error('Error fetching saved report filter options:', error);
    throw error;
  }
};

